import { useEffect, useRef } from 'react'
import { Alert } from '../components/Alert'
import { BbIcon } from '../components/BbIcon'
import { useCompanies } from '../companies/useCompanies'
import { useConnectCompany } from '../companies/useConnectCompany'

/* First-run guidance for a tenant with no connected company.

   Rendered by RequireCompany in place of the gated page, so it behaves like a
   blocking dialog rather than a route: there is no dismiss action and the scrim
   does not close it, because the app genuinely has nothing to offer until a
   company exists. The only ways out are connecting, or signing out — which stays
   reachable in the top bar behind it.

   No prototype exists for this screen; it is built from the BatchBooks tokens and
   the login screen's navy panel so it reads as the same product.

   Styling note: the rules in batchbooks.css are prefixed with `.bb-welcome-*`,
   which is load-bearing rather than cosmetic. The reset at `.bb :where(h1,h2,h3,h4)`
   has a class in it, so a bare descendant selector of equal weight loses to it and
   the text here would render in the on-surface ink instead of the hero's white. */

/** What connecting unlocks, in the order a new user meets it. */
const STEPS: Array<{ icon: 'upload' | 'edit-note' | 'download' | 'trash'; title: string; body: string }> = [
  {
    icon: 'upload',
    title: 'Import',
    body: 'Push spreadsheets of customers, invoices or bills into QuickBooks, row by row, with errors flagged before anything is written.',
  },
  {
    icon: 'edit-note',
    title: 'Modify',
    body: 'Correct existing transactions in bulk, instead of opening them one at a time.',
  },
  {
    icon: 'download',
    title: 'Export',
    body: 'Pull transactions and lists out as a workbook, for analysis or a safe backup.',
  },
  {
    icon: 'trash',
    title: 'Delete',
    body: 'Clear duplicates and stale entries in bulk, with the count shown before anything is removed.',
  },
]

export function WelcomeDialog() {
  const { loading, reload } = useCompanies()
  const { connect, connecting, error } = useConnectCompany()
  const dialogRef = useRef<HTMLDivElement>(null)

  // A dialog that cannot be dismissed still has to take focus, or a keyboard
  // user is left interacting with the page behind it.
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  return (
    // `bb` on both nodes is what makes the --bb-* tokens resolve: they are scoped
    // to `.bb` (batchbooks.css) rather than :root, so without it every var() in
    // these rules is invalid and the dialog renders as unstyled layout.
    <div className="bb scrim bb-welcome-scrim">
      <div
        className="bb bb-welcome-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        {/* Two columns from 900px up: the navy panel carries the pitch and the
            white side carries the single action, so the eye has one place to
            land instead of a centred stack. */}
        <aside className="bb-welcome-hero">
          <span className="bb-welcome-brand">
            <BbIcon name="chart" />
            BatchBooks
          </span>

          <h2 className="bb-welcome-title" id="welcome-title">
            Connect your QuickBooks company
          </h2>
          <p className="bb-welcome-lede">
            BatchBooks works on the books of a QuickBooks Online company, so it needs to know which
            one. Connecting takes about a minute, and you can add more companies later.
          </p>

          <ul className="bb-welcome-steps">
            {STEPS.map((step) => (
              <li key={step.title}>
                <span className="bb-welcome-step-ico">
                  <BbIcon name={step.icon} />
                </span>
                <span className="bb-welcome-step-copy">
                  <b>{step.title}</b>
                  <span>{step.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </aside>

        <div className="bb-welcome-body">
          <h3 className="bb-welcome-body-title">Ready when you are</h3>
          <p className="bb-welcome-body-copy">
            You will be asked to sign in to Intuit and approve access. BatchBooks reads and writes
            only the company you choose, and you can disconnect it at any time.
          </p>

          {error ? (
            <div className="bb-welcome-err">
              <Alert tone="err" title="Could not start the connection">
                {error}
              </Alert>
            </div>
          ) : null}

          <div className="bb-welcome-cta">
            <button
              className="bb-btn bb-btn-green bb-btn-block"
              type="button"
              disabled={connecting}
              onClick={() => void connect()}
            >
              <BbIcon name="plus" />
              {connecting ? 'Opening QuickBooks…' : 'Connect a company'}
            </button>

            <button
              className="bb-alink bb-alink-quiet"
              type="button"
              disabled={loading}
              onClick={reload}
            >
              {loading ? 'Checking…' : 'Already connected? Refresh'}
            </button>
          </div>

          <ul className="bb-welcome-assure">
            <li>
              <BbIcon name="shield-check" />
              Encrypted at rest, and revocable any time
            </li>
            <li>
              <BbIcon name="check-circle" />
              Nothing is written until you confirm it
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
